/* @flow */

import * as React from 'react';
import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import './ToastContainerWrapper.css';

type ToastContainerWrapperProps = {
  +children: React.Node,
};

const ToastContainerWrapper = ({ children }: ToastContainerWrapperProps): React.Node => {
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    // Create a dedicated top-layer container attached directly to <body>
    const div = document.createElement('div');
    div.setAttribute('popover', 'manual');
    div.className = 'toastContainerWrapper';

    document.body?.appendChild(div);

    // Promote to Top Layer
    if (typeof div.showPopover === 'function') {
      try {
        div.showPopover();
      } catch {
        // Fallback for non-supported browsers
      }
    }

    setContainer(div);

    const bringToFront = () => {
      if (typeof div.showPopover === 'function') {
        try {
          div.hidePopover();
          div.showPopover();
        } catch {
          // Ignore state mismatch errors
        }
      }
    };

    // Re-promote toast layer whenever a new modal dialog opens
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'open') {
          const target = mutation.target;
          if (target instanceof HTMLElement && target.tagName === 'DIALOG' && target.hasAttribute('open')) {
            bringToFront();
          }
        }
      }
    });

    const targetElement = document.body || document.documentElement;
    if (targetElement) {
      observer.observe(targetElement, {
        attributeFilter: ['open'],
        attributes: true,
        subtree: true,
      });
    }

    return () => {
      observer.disconnect();
      if (div.parentNode) {
        div.parentNode.removeChild(div);
      }
    };
  }, []);

  if (!container) {
    return null;
  }

  // Wrap children in a container with pointerEvents 'auto' so toasts are clickable
  return createPortal(<div className='toastContainerWrapperPortal'>{children}</div>, container);
};

export default ToastContainerWrapper;
